import { HttpDeprecatedResult, toHttpDeprecatedResult } from '@commons/core/transport/http-deprecated.service';
import { RxUtil } from '@commons/util/utilities/rx';
import { ILogService, IPromise, IQService } from 'angular';
import { map, Observable, switchMap, tap } from 'rxjs';

import { AgentStatus, AgentUserFacade, IStateEvent, PositionType, StmService, Transition, UserStmService } from '@contact/upgrade';

const MODULE_NAME = 'agentStatusDeprecatedService';

/**
 * @deprecated prefer AgentUserFacade
 */
export interface IAgentStatusDeprecatedService {
  /**
   * @deprecated prefer AgentUserFacade.transitionCreated$
   */
  created(): Observable<IStateEvent<AgentStatus>>;

  /**
   * @deprecated prefer AgentUserFacade.transitionSuccess$
   */
  success(): Observable<IStateEvent<AgentStatus>>;

  /**
   * @deprecated prefer AgentUserFacade.transitionFailure$
   */
  failure(): Observable<IStateEvent<AgentStatus>>;

  /**
   * @deprecated prefer AgentUserFacade.transition$
   */
  getCurrentAgentState(): AgentStatus;

  /**
   * @deprecated prefer AgentUserFacade.connect
   */
  connect(): IPromise<any>;

  /**
   * @deprecated prefer AgentUserFacade.disconnect
   */
  disconnect(): IPromise<any>;

  /**
   * @deprecated prefer AgentUserFacade.available
   */
  available(isSupervisor: boolean): IPromise<any>;

  /**
   * @deprecated prefer AgentUserFacade.availableAndClose
   */
  availableAndClose(): IPromise<any>;

  /**
   * @deprecated prefer AgentUserFacade.available
   */
  availableOnPosition(positionId: string, positionType: PositionType): IPromise<any>;

  /**
   * @deprecated prefer AgentUserFacade
   */
  notWorkingAfterBurnt(): IPromise<any>;
}

export class AgentStatusDeprecatedService implements IAgentStatusDeprecatedService {
  static $inject: Array<string> = ['$log', '$q', 'agentUserFacade', 'agentStatusService', 'stmService'];

  public name = MODULE_NAME;

  private transition: Transition;

  constructor(
    protected readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly agentUserFacade: AgentUserFacade,
    private readonly agentStatusService: UserStmService,
    private readonly stmService: StmService
  ) {
    this.agentUserFacade.transition$.subscribe(transition => (this.transition = transition));
  }

  /**
   * @deprecated prefer AgentUserFacade.transitionCreated$
   */
  created() {
    return this.agentUserFacade.created$.pipe(map(transition => this.toEvent(transition)));
  }

  /**
   * @deprecated prefer AgentUserFacade.transitionSuccess$
   */
  success() {
    return this.agentUserFacade.success$.pipe(map(transition => this.toEvent(transition)));
  }

  /**
   * @deprecated prefer AgentUserFacade.transitionFailure$
   */
  failure() {
    return this.agentUserFacade.failure$.pipe(
      map(transition => ({
        ...transition,
        current: transition.error!,
      })),
      map(transition => this.toEvent(transition))
    );
  }

  /**
   * @deprecated prefer AgentUserFacade.transition$
   */
  getCurrentAgentState(): AgentStatus {
    return this.transition && AgentStatus[this.transition.current];
  }

  /**
   * @deprecated prefer AgentUserFacade.connect
   */
  connect(): IPromise<HttpDeprecatedResult> {
    let ob$ = this.stmService.checkTransition(this.transition, AgentStatus.CONNECTED).pipe(
      switchMap(() => this.agentStatusService.connect()),
      toHttpDeprecatedResult()
    );
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated prefer AgentUserFacade.disconnect
   */
  disconnect(): IPromise<HttpDeprecatedResult> {
    let ob$ = this.agentStatusService.disconnect().pipe(toHttpDeprecatedResult());
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated prefer AgentUserFacade.available
   */
  available(isSupervisor: boolean): IPromise<HttpDeprecatedResult> {
    let ob$: Observable<any>;
    if (isSupervisor) {
      ob$ = this.agentStatusService.available('', PositionType.SUPERVISOR).pipe(toHttpDeprecatedResult());
    } else {
      ob$ = this.stmService.checkTransition(this.transition, AgentStatus.AVAILABLE).pipe(
        switchMap(() => this.agentStatusService.available()),
        toHttpDeprecatedResult()
      );
    }
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated prefer AgentUserFacade.available
   */
  availableOnPosition(positionId: string, positionType: PositionType): IPromise<HttpDeprecatedResult> {
    let ob$ = this.stmService.checkTransition(this.transition, AgentStatus.AVAILABLE).pipe(
      switchMap(() => this.agentStatusService.available(positionId, positionType)),
      toHttpDeprecatedResult()
    );
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated prefer AgentUserFacade.availableAndClose
   */
  availableAndClose(): IPromise<HttpDeprecatedResult> {
    let ob$ = this.stmService.checkTransition(this.transition, AgentStatus.AVAILABLE).pipe(
      switchMap(() => this.agentStatusService.availableAndClose()),
      toHttpDeprecatedResult()
    );
    return RxUtil.toPromise(ob$, this.$q);
  }

  /**
   * @deprecated prefer AgentUserFacade
   */
  notWorkingAfterBurnt(): IPromise<any> {
    // if transition to NOT WORKING is allowed we send change state
    let ob$ = this.stmService
      .checkTransition(this.transition, AgentStatus.NOT_WORKING)
      .pipe(tap(() => this.agentUserFacade.updateTransition(AgentStatus.NOT_WORKING)));
    return RxUtil.toPromise(ob$, this.$q);
  }

  private toEvent({ id, date, previous, current }: Transition): IStateEvent<AgentStatus> {
    return {
      id: id,
      timestamp: new Date(date),
      oldState: previous && AgentStatus[previous],
      newState: current && AgentStatus[current],
    };
  }
}

export default MODULE_NAME;
